import { useState } from "react";
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme,
  Divider,
  Drawer,
  Tooltip,
} from "@mui/material";
import {
  Dashboard,
  Business,
  Gavel,
  
  VerifiedUser,
  Person,
  
  Menu as MenuIcon,
  Close,
} from "@mui/icons-material";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

const AdminSidebar = ({ navbarHeight = 64 }) => {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setCollapsed(!collapsed);
    }
  };

  const dashboardItems = [
    { title: "Admin Dashboard", icon: <Dashboard />, path: "/superadmin" },
    { title: "CS Dashboard", icon: <Business />, path: "/csdashboard" },
    { title: "CA Dashboard", icon: <Business />, path: "/Cadashboard" },
    { title: "Legal Dashboard", icon: <Gavel />, path: "/Legal" },
    { title: "Compliance Dashboard", icon: <VerifiedUser />, path: "/compliance" },
    { title: "User Dashboard", icon: <Person />, path: "/KycManagement" },
  ];

  const portalItems = [
    
  ];

  const drawerWidth = collapsed ? 72 : 280;

  const renderMenuItem = (item) => (
    <ListItemButton
      key={item.title}
      component={Link}
      to={item.path}
      selected={location.pathname === item.path}
      onClick={() => isMobile && setMobileOpen(false)}
      sx={{
        minHeight: 48,
        px: collapsed ? 1.5 : 2.5,
        margin: "4px 16px",
        borderRadius: "8px",
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          backgroundColor: theme.palette.action.hover,
          transform: "translateX(4px)",
          "& .MuiListItemText-primary": {
            color: theme.palette.primary.main,
            fontWeight: "bold",
          },
          "& .MuiListItemIcon-root": {
            color: theme.palette.primary.main,
          },
        },
        "&.Mui-selected": {
          backgroundColor: theme.palette.action.selected,
          "& .MuiListItemText-primary": {
            color: theme.palette.primary.main,
            fontWeight: "bold",
          },
          "& .MuiListItemIcon-root": {
            color: theme.palette.primary.main,
          },
        },
      }}
    >
      <ListItemIcon
        sx={{
          minWidth: 0,
          mr: collapsed ? 0 : 3,
          justifyContent: "center",
          color: theme.palette.primary.main,
        }}
      >
        <Tooltip
          title={item.title}
          placement="right"
          disableHoverListener={!collapsed}
        >
          {item.icon}
        </Tooltip>
      </ListItemIcon>
      {!collapsed && (
        <ListItemText
          primary={item.title}
          sx={{
            "& .MuiListItemText-primary": {
              fontSize: "0.875rem",
              fontWeight: 500,
            },
          }}
        />
      )}
    </ListItemButton>
  );

  const drawer = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Header */}
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "space-between",
          borderBottom: "1px solid",
          borderColor: "divider",
          minHeight: 64,
          flexShrink: 0,
          position: "relative",
        }}
      >
        {isMobile && (
          <IconButton
            sx={{
              position: "absolute",
              right: 8,
              top: "50%",
              transform: "translateY(-50%)",
            }}
            onClick={() => setMobileOpen(false)}
          >
            <Close />
          </IconButton>
        )}
        
        {!collapsed && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <Box
              component={Link}
              to="/Dashboard"
              sx={{
                display: "flex",
                alignItems: "center",
                textDecoration: "none",
                color: "inherit",
                mr: 1,
              }}
            >
              <Box
                component="img"
                src="/failogo.jpg"
                alt="Logo"
                sx={{
                  height: 32,
                  width: 32,
                  mr: 1,
                }}
              />
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.light}, ${theme.palette.warning.main})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                display: "inline-block",
              }}
            >
              Admin Portal
            </Typography>
          </Box>
        )}
        
        {!isMobile && (
          <IconButton onClick={handleDrawerToggle} size="small">
            <MenuIcon />
          </IconButton>
        )}
      </Box>

      {/* Menu Items */}
      <Box
        sx={{
          flexGrow: 1,
          overflow: "auto",
        }}
      >
        <List>
          {dashboardItems.map(renderMenuItem)}
          <Divider sx={{ my: 2, mx: 2 }} />
          {portalItems.map(renderMenuItem)}
        </List>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          borderTop: "1px solid",
          borderColor: "divider",
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          minHeight: {
            xs: 120,
            sm: 140,
            md: collapsed ? 100 : 160,
            lg: 180,
          },
          flexShrink: 0,
          backgroundColor: theme.palette.background.default,
        }}
      >
        <Box
          component="img"
          src="/icon.jpg"
          alt="Company Logo"
          sx={{
            height: {
              xs: collapsed ? 40 : 60,
              sm: collapsed ? 50 : 70,
              md: collapsed ? 60 : 90,
              lg: collapsed ? 70 : 120,
            },
            width: "auto",
            objectFit: "contain",
            maxWidth: {
              xs: collapsed ? "33px" : "148px",
              sm: collapsed ? "38px" : "178px",
              md: collapsed ? "43px" : "198px",
              lg: collapsed ? "48px" : "218px",
            },
            display: "block",
            mt: { xs: 0.5, sm: 1 },
          }}
        />
      </Box>
    </Box>
  );

  return (
    <>
      {isMobile && (
        <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="start"
          onClick={() => setMobileOpen(!mobileOpen)}
          sx={{
            position: 'fixed',
            left: 16,
            top: navbarHeight + 16,
            zIndex: 1100,
          }}
        >
          <MenuIcon />
        </IconButton>
      )}

      <AnimatePresence>
        {isMobile ? (
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              display: { xs: 'block', md: 'none' },
              '& .MuiDrawer-paper': {
                width: 280,
                boxSizing: 'border-box',
                backgroundColor: theme.palette.background.paper,
                borderRight: '1px solid',
                borderColor: theme.palette.divider,
                boxShadow: theme.shadows[4],
                top: navbarHeight,
                height: `calc(100vh - ${navbarHeight}px)`,
              },
            }}
          >
            {drawer}
          </Drawer>
        ) : (
          <motion.div
            animate={{ width: drawerWidth }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            style={{
              width: drawerWidth,
              flexShrink: 0,
              height: `calc(100vh - ${navbarHeight}px)`,
              position: "relative",
            }}
          >
            <Box
              sx={{
                width: drawerWidth,
                height: `calc(100vh - ${navbarHeight}px)`,
                borderRight: "1px solid",
                borderColor: theme.palette.divider,
                backgroundColor: theme.palette.background.paper,
                overflow: "hidden",
                position: "fixed",
                top: navbarHeight,
                left: 0,
                bottom: 0,
                zIndex: 1100,
                display: "flex",
                flexDirection: "column",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  right: 0,
                  bottom: 0,
                  width: !collapsed ? "3px" : "0px",
                  backgroundColor: theme.palette.primary.main,
                  transition: "width 0.3s ease-in-out",
                  zIndex: 1,
                },
              }}
            >
              {drawer}
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AdminSidebar;